/**
 * Утилиты для форматирования данных
 */

/**
 * Форматирует дату в формат DD.MM.YYYY
 * 
 * @param date - Дата для форматирования
 * @returns Отформатированная строка даты
 * 
 * @example
 * formatDate(new Date('2025-10-30')) // '30.10.2025'
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return 'Неверная дата';
  }
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}.${month}.${year}`;
}

/**
 * Форматирует сумму в валюту с символом рубля
 * 
 * @param amount - Сумма для форматирования
 * @param options - Опции форматирования
 * @param options.showCurrency - Показывать ли символ валюты (по умолчанию true)
 * @param options.decimals - Количество знаков после запятой (по умолчанию 0)
 * @returns Отформатированная строка суммы
 * 
 * @example
 * formatCurrency(1000) // '1 000 ₽'
 * formatCurrency(1234.56, { decimals: 2 }) // '1 234,56 ₽'
 * formatCurrency(500, { showCurrency: false }) // '500'
 */
export function formatCurrency(
  amount: number,
  options: { showCurrency?: boolean; decimals?: number } = {}
): string {
  const { showCurrency = true, decimals = 0 } = options;
  
  if (isNaN(amount)) {
    return showCurrency ? '0 ₽' : '0';
  }
  
  // Форматируем число с разделителями тысяч
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
  
  return showCurrency ? `${formatted} ₽` : formatted;
}

/**
 * Форматирует номер телефона в формат +7 (XXX) XXX-XX-XX
 * 
 * @param phone - Номер телефона для форматирования (может быть с +7 или без)
 * @returns Отформатированный номер телефона
 * 
 * @example
 * formatPhone('79001234567') // '+7 (900) 123-45-67'
 * formatPhone('+79001234567') // '+7 (900) 123-45-67'
 * formatPhone('9001234567') // '+7 (900) 123-45-67'
 */
export function formatPhone(phone: string): string {
  if (!phone) {
    return '';
  }
  
  // Убираем все нецифровые символы
  const digits = phone.replace(/\D/g, '');
  
  // Если номер начинается с 8, заменяем на 7
  let normalizedDigits = digits;
  if (digits.startsWith('8') && digits.length === 11) {
    normalizedDigits = '7' + digits.slice(1);
  }
  
  // Если номер без кода страны (10 цифр), добавляем 7
  if (normalizedDigits.length === 10) {
    normalizedDigits = '7' + normalizedDigits;
  }
  
  // Проверяем, что у нас 11 цифр и номер начинается с 7
  if (normalizedDigits.length !== 11 || !normalizedDigits.startsWith('7')) {
    return phone; // Возвращаем исходный номер, если формат неверный
  }
  
  // Форматируем: +7 (XXX) XXX-XX-XX
  const code = normalizedDigits.slice(1, 4);
  const part1 = normalizedDigits.slice(4, 7);
  const part2 = normalizedDigits.slice(7, 9);
  const part3 = normalizedDigits.slice(9, 11);
  
  return `+7 (${code}) ${part1}-${part2}-${part3}`;
}
